import { StatusCode } from '@/constants/statuses';
import {
  AccessProgramType,
  ActivityType,
  ContactVm,
  DictionaryDTO,
  OrderStatus,
  ProductVm,
  ProgramType,
  ProposalCloningStatus,
  ProposalType,
  ProposalVm,
  TaskCardStatus,
  WatcherVm,
} from '@/types';

// duplicated from backend types
export type Proposal = {
  id: string;
  name: string;
  systemSize: number;
  equipmentPrice: number;
  servicesPrice: number;
  link?: string;
  homeownerFirstName: string | null;
  homeownerLastName: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  statusColor: string;
  status: DictionaryDTO['id'];
  createOn: number | string;
  number: string;
  pricePerWatt: number | null;
  proposalType: ProposalType;
  proposalValidThroughDate?: string;
  totalPriceForTheHomeOwner?: number;
  itc?: number | null;
  proposalVersion?: string;
  newVersionRequirements?: [];
  solarDesign: {
    id: string;
    aerialSiteImage: string | null;
    externalId: string;
    name: string;
  } | null;
  isEligibilityChecksEnforced: boolean;
  showRefreshModal?: boolean;
  timberlineSolarESPricePerWatt?: number;
  timberlineSolarHDZPricePerSquare: number;
  solarWarrantyPrice: number | null;
  zone2AdderQuantity: number | null;
  timberlineShingles: number | null;
  timberlineSquares: number | null;
  mainServicePanelUpgradePrice: number | null;
  multiArrayAdderQuantity: number | null;
  balanceSystemRemovalPrice: number | null;
  electricVehicleWallPlugPrice: number | null;
  zone2AdderUnitPrice: number | null;
  multiArrayAdderUnitPrice: number | null;
  proposalCloningStatus: ProposalCloningStatus | null;
  essentialsBackupPackageRooferPrice: number | null;
  maxBackupPackageRooferPrice: number | null;
  grandTotal: number | null;
  outdatedGrandTotal: number | null;
  solarLaborPricePerSquare: number | null;
  solarMarginPercent: number | null;
  solarMarginPrice: number | null;
  wasteFactorPercent: number | null;
  asphaltPricePerSquare: number | null;
  roofLaborCosts: number | null;
  roofLaborPricePerSquare: number | null;
  roofSizeArea: number | null;
  totalRoofCosts: number | null;
  totalRoofPrice: number | null;
  roofWarrantyCosts: number | null;
  roofMaterialsCosts: number | null;
  roofMarginPercent: number | null;
  roofMarginPrice: number | null;
  totalTimberlineSolarESPrice: number | null;
  totalTimberlineSolarHDZPrice: number | null;
  additionalProjectCosts: number | null;
  areDistroProductsRepricing?: boolean | null;
  areDirectProductsRepricing?: boolean | null;
  solarProduct?: number;
};

export enum ProjectNextStep {
  None = 0,
  Schedule = 1,
  Install = 2,
}

export enum ActivityInstance {
  Order,
  Opportunity,
  Lead,
  OrderWithoutProject = 3,
}

export type CardStatus = {
  order: StatusCode | null;
  permit: StatusCode | null;
  delivery: StatusCode | null;
  install: StatusCode | null;
  inspection: StatusCode | null;
  activation: StatusCode | null;
};

export enum CalculationMethod {
  SKU,
  DollarPerWatt,
}

export type ProposalWithBom = ProposalVm & {
  priceCalculationMethod: CalculationMethod;
  estimatedSolarMaterialsCost: number;
};

export type ProductWithBom = ProductVm & {
  isBomComponent: boolean;
};

export type Activity = {
  id: string;
  accessProgram: AccessProgramType | null;
  contact: ContactVm;
  proposal: Proposal | null;
  statusId: number;
  statusColor: string;
  typeId: ActivityType;
  instanceId: ActivityInstance;
  createdOn: string;
  modifiedOn: string;
  orderDisplayId: string; // Sales order id
  orderPurchaseNumber: string; // PO number
  orderEstimatedDeliveryDate: string | null;
  orderInstallDate: string | null;
  starred: boolean;
  // Jobs related fields
  orderStatus: OrderStatus | null;
  programType: ProgramType;
  hasAlert?: boolean;
  cardStatuses?: CardStatus;
  messageBoardId: string | null;
  isEligibilityChecksEnforced: boolean;
  orderNumber?: string | null;
  opportunityId?: string | null;
  isLeanForward?: boolean;
  leanForwardHomeOwnerGuarantee?: 'Active' | 'Void' | null;
  projectNextStep?: ProjectNextStep;
  customerContactTaskCardStatus: TaskCardStatus | null;
  utilityBillTaskCardStatus: TaskCardStatus | null;
  siteSurveyTaskCardStatus: TaskCardStatus | null;
  watcherList?: WatcherVm[];
};

export enum ActivitySortingType {
  OpenTasks,
  RecentActivity,
  Newest,
  NameAtoZ,
}

export type ActivityListFilters = {
  statuses: number[];
  sortingType: ActivitySortingType;
  type: number;
  searchQuery: string;
  starredOnly: boolean;
};

export type AppliedActivityListFilters = {
  statuses?: number[];
  sortingType?: ActivitySortingType;
  page?: number;
  type?: number;
  searchQuery?: string;
  starredOnly?: boolean;
};

export type InfoToFetchActivityData = {
  activityType: ActivityInstance;
  activityId: string;
};

export type ProposalQuery = {
  proposalId: string;
  designId: string;
};

export type ProposalStatusQuery = {
  id: string;
  solarDesignId: string;
  proposalCloningStatus: ProposalCloningStatus;
  // ... other fields that we don't need
};

export type dateType = 'DELIVERY' | 'INSTALL';

export type AvailableDates = {
  firstAvailableDate: string;
  excludeDates: string[];
  enableWeekends: boolean;
};

export type Params = Record<string, number | string>;

export type SiteSurveyAsServiceInfo = {
  appointmentDate: string | null;
  appointmentTime: string | null;
  vendor: string | null;
  isEnabled: boolean;
};
